import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronDown, Calendar } from "lucide-react";

const DateRangeFilter = ({ onRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("last7days");
  const [showCustomPickers, setShowCustomPickers] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const dropdownRef = useRef(null);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const calculateDates = (option) => {
    const today = new Date();
    const endDate = new Date(today);
    let startDate = new Date(today);
    
    switch(option) {
      case "last3days":
        startDate.setDate(today.getDate() - 3);
        break;
      case "last7days":
        startDate.setDate(today.getDate() - 7);
        break;
      case "last14days":
        startDate.setDate(today.getDate() - 14);
        break;
      case "last30days":
        startDate.setDate(today.getDate() - 30);
        break;
      case "last90days":
        startDate.setDate(today.getDate() - 90);
        break;
      case "last180days":
        startDate.setDate(today.getDate() - 180);
        break;
      case "last365days":
        startDate.setDate(today.getDate() - 365);
        break;
      default:
        startDate.setDate(today.getDate() - 7);
    }
    
    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setShowCustomPickers(false);
    
    if (option === "custom") {
      setShowCustomPickers(true);
      setCustomStartDate(null);
      setCustomEndDate(null);
    } else {
      const { startDate, endDate } = calculateDates(option);
      onRangeChange(startDate, endDate);
      setIsOpen(false);
    }
  };

  const handleCustomApply = () => {
    if (customStartDate && customEndDate) {
      onRangeChange(formatDate(customStartDate), formatDate(customEndDate));
      setIsOpen(false);
      setShowCustomPickers(false);
    }
  };

  useEffect(() => {
    const { startDate, endDate } = calculateDates("last7days");
    onRangeChange(startDate, endDate);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCustomPickers(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getButtonLabel = () => {
    if (selectedOption === "custom") return "Custom Range";
    switch(selectedOption) {
      case "last3days": return "Last 3 Days";
      case "last7days": return "Last 7 Days";
      case "last14days": return "Last 14 Days";
      case "last30days": return "Last Month";
      case "last90days": return "Last 3 Months";
      case "last180days": return "Last 6 Months";
      case "last365days": return "Last Year";
      default: return "Last 7 Days";
    }
  };

  return (
    <div className="relative inline-block w-full sm:w-auto" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-cyan hover:bg-primary-cyan/85 text-white px-4 sm:px-5 py-2 rounded-xl font-medium transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap text-sm sm:text-base"
      >
        <Calendar size={16} className="sm:w-[18px] sm:h-[18px]" />
        <span className="truncate">{getButtonLabel()}</span>
        <ChevronDown size={14} className={`transition-transform duration-200 sm:w-4 sm:h-4 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 sm:right-auto mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-20 min-w-[200px] sm:min-w-[220px] overflow-hidden">
          <div className="py-2">
            <button
              onClick={() => handleOptionClick("last3days")}
              className={`w-full text-left px-4 sm:px-5 py-2 text-xs sm:text-sm transition-colors whitespace-nowrap ${selectedOption === "last3days" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 3 Days
            </button>
            <button
              onClick={() => handleOptionClick("last7days")}
              className={`w-full text-left px-4 sm:px-5 py-2 text-xs sm:text-sm transition-colors whitespace-nowrap ${selectedOption === "last7days" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 7 Days
            </button>
            <button
              onClick={() => handleOptionClick("last14days")}
              className={`w-full text-left px-4 sm:px-5 py-2 text-xs sm:text-sm transition-colors whitespace-nowrap ${selectedOption === "last14days" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 14 Days
            </button>
            <button
              onClick={() => handleOptionClick("last30days")}
              className={`w-full text-left px-4 sm:px-5 py-2 text-xs sm:text-sm transition-colors whitespace-nowrap ${selectedOption === "last30days" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last Month
            </button>
            <button
              onClick={() => handleOptionClick("last90days")}
              className={`w-full text-left px-4 sm:px-5 py-2 text-xs sm:text-sm transition-colors whitespace-nowrap ${selectedOption === "last90days" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 3 Months
            </button>
            <button
              onClick={() => handleOptionClick("last180days")}
              className={`w-full text-left px-4 sm:px-5 py-2 text-xs sm:text-sm transition-colors whitespace-nowrap ${selectedOption === "last180days" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 6 Months
            </button>
            <button
              onClick={() => handleOptionClick("last365days")}
              className={`w-full text-left px-4 sm:px-5 py-2 text-xs sm:text-sm transition-colors whitespace-nowrap ${selectedOption === "last365days" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last Year
            </button>
            <div className="border-t border-gray-100 my-1"></div>
            <button
              onClick={() => handleOptionClick("custom")}
              className={`w-full text-left px-4 sm:px-5 py-2 text-xs sm:text-sm transition-colors whitespace-nowrap ${selectedOption === "custom" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Custom Range
            </button>
          </div>

          {showCustomPickers && (
            <div className="border-t border-gray-100 p-3 sm:p-4 bg-gray-50">
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Start Date</label>
                <DatePicker
                  selected={customStartDate}
                  onChange={(date) => setCustomStartDate(date)}
                  dateFormat="dd/MM/yyyy"
                  maxDate={customEndDate || new Date()}
                  className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 focus:border-primary-cyan bg-white"
                  placeholderText="Select start date"
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">End Date</label>
                <DatePicker
                  selected={customEndDate}
                  onChange={(date) => setCustomEndDate(date)}
                  dateFormat="dd/MM/yyyy"
                  maxDate={new Date()}
                  minDate={customStartDate}
                  className="w-full px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 focus:border-primary-cyan bg-white"
                  placeholderText="Select end date"
                />
              </div>
              <button
                onClick={handleCustomApply}
                disabled={!customStartDate || !customEndDate}
                className="w-full bg-primary-cyan hover:bg-primary-cyan/85 text-white px-4 py-1.5 sm:py-2 rounded-lg font-medium text-xs sm:text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
              >
                Apply Range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;